import React, { useEffect, useRef, useState } from 'react';
import {
    GameResultOverlay,
    GameShell,
    clamp,
} from '../shared';
import {
    createTrackProfiles,
    cursorIsOutside,
    trackOffset,
} from './dual-track/trackModel';
import brainGameLevels from '../../../lib/brainGameLevels';

const RUN_DURATION_MS = 60000;
const METRIC_INTERVAL_MS = 250;
const { normalizeBrainGameLevel } = brainGameLevels;

const DUAL_TRACK_SPEED_BY_LEVEL = [0.55, 0.7, 0.85, 1, 1.1, 1.2, 1.3, 1.4, 1.5];

function DualControl({ copy = {}, index, isOutside, onChange, value }) {
    const travelStartRef = useRef(null);
    const travelEndRef = useRef(null);

    const updateFromPointer = (event) => {
        const startBounds = travelStartRef.current?.getBoundingClientRect();
        const endBounds = travelEndRef.current?.getBoundingClientRect();
        if (!startBounds || !endBounds) {
            return;
        }

        const startX = startBounds.left + (startBounds.width / 2);
        const startY = startBounds.top + (startBounds.height / 2);
        const axisX = endBounds.left + (endBounds.width / 2) - startX;
        const axisY = endBounds.top + (endBounds.height / 2) - startY;
        const axisLengthSquared = (axisX * axisX) + (axisY * axisY);
        if (axisLengthSquared < 1) {
            return;
        }

        const pointerX = event.clientX - startX;
        const pointerY = event.clientY - startY;
        const progress = clamp(
            ((pointerX * axisX) + (pointerY * axisY)) / axisLengthSquared,
            0,
            1,
        );
        onChange(index, (progress * 2) - 1);
    };

    const handlePointerDown = (event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        updateFromPointer(event);
    };

    const handlePointerMove = (event) => {
        if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
            return;
        }
        event.preventDefault();
        updateFromPointer(event);
    };

    const handleKeyDown = (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) {
            return;
        }
        event.preventDefault();
        onChange(index, clamp(value + (event.key === 'ArrowLeft' ? -0.08 : 0.08), -1, 1));
    };

    return (
        <div
            aria-label={index === 0
                ? copy.leftTrackControl
                : copy.rightTrackControl}
            aria-valuemax="100"
            aria-valuemin="0"
            aria-valuenow={Math.round((value + 1) * 50)}
            className={`UFOV-brain-games__dual-control is-${index === 0 ? 'left' : 'right'} ${isOutside ? 'is-outside' : ''}`.trim()}
            onKeyDown={handleKeyDown}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            role="slider"
            tabIndex="0"
        >
            <span aria-hidden="true" className="UFOV-brain-games__dual-control-rail" />
            <span aria-hidden="true" className="UFOV-brain-games__dual-control-travel">
                <span
                    className="UFOV-brain-games__dual-control-endpoint is-start"
                    ref={travelStartRef}
                />
                <span
                    className="UFOV-brain-games__dual-control-endpoint is-end"
                    ref={travelEndRef}
                />
                <span
                    className="UFOV-brain-games__dual-control-thumb"
                    style={{ left: `${(value + 1) * 50}%` }}
                >
                    <i />
                </span>
            </span>
        </div>
    );
}

function DualTrackGame({
    copy = {},
    isPaused = false,
    onRunComplete = null,
    runDurationMs = RUN_DURATION_MS,
    showResults = true,
    speedMultiplier = null,
    initialLevel,
    ...props
}) {
    const level = normalizeBrainGameLevel('dual-track', initialLevel);
    const durationMs = Math.max(5000, Number(runDurationMs) || RUN_DURATION_MS);
    const configuredPace = Number(speedMultiplier);
    const pace = clamp(
        Number.isFinite(configuredPace) && configuredPace > 0
            ? configuredPace
            : DUAL_TRACK_SPEED_BY_LEVEL[level - 1],
        0.35,
        1.5
    );
    const canvasRef = useRef(null);
    const frameRef = useRef(0);
    const pausedRef = useRef(isPaused);
    pausedRef.current = isPaused;
    const phaseRef = useRef('running');
    const lastFrameRef = useRef(0);
    const elapsedRef = useRef(0);
    const trackedRef = useRef(0);
    const insideRef = useRef(0);
    const departuresRef = useRef(0);
    const lastMetricRef = useRef(0);
    const outsideRef = useRef([false, false]);
    const controlsRef = useRef([0, 0]);
    const profilesRef = useRef(createTrackProfiles(Date.now()));
    const completionReportedRef = useRef(false);
    const [phase, setPhase] = useState('running');
    const [controls, setControls] = useState([0, 0]);
    const [outside, setOutside] = useState([false, false]);
    const [metrics, setMetrics] = useState({
        durationMs,
        level,
        maxScore: 100,
        mistakes: 0,
        score: 100,
        timeRemainingMs: durationMs,
    });

    const publishMetrics = () => {
        const score = trackedRef.current > 0
            ? Math.round((insideRef.current / trackedRef.current) * 100)
            : 100;
        setMetrics({
            durationMs,
            level,
            maxScore: 100,
            mistakes: departuresRef.current,
            score,
            timeRemainingMs: Math.max(0, durationMs - elapsedRef.current),
        });
    };

    const setControl = (index, value) => {
        if (pausedRef.current) {
            return;
        }

        const next = [...controlsRef.current];
        next[index] = clamp(value, -1, 1);
        controlsRef.current = next;
        setControls(next);
    };

    const reset = () => {
        completionReportedRef.current = false;
        elapsedRef.current = 0;
        trackedRef.current = 0;
        insideRef.current = 0;
        departuresRef.current = 0;
        lastMetricRef.current = 0;
        lastFrameRef.current = performance.now();
        controlsRef.current = [0, 0];
        outsideRef.current = [false, false];
        profilesRef.current = createTrackProfiles(Date.now());
        setControls([0, 0]);
        setOutside([false, false]);
        setMetrics({
            durationMs,
            level,
            maxScore: 100,
            mistakes: 0,
            score: 100,
            timeRemainingMs: durationMs,
        });
        phaseRef.current = 'running';
        setPhase('running');
    };

    useEffect(() => {
        const draw = (now) => {
            const canvas = canvasRef.current;
            const context = canvas?.getContext('2d');
            const cssWidth = canvas?.clientWidth || 0;
            const cssHeight = canvas?.clientHeight || 0;
            const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
            let delta = lastFrameRef.current ? Math.min(40, now - lastFrameRef.current) : 0;
            lastFrameRef.current = now;

            if (canvas && context && cssWidth > 0 && cssHeight > 0) {
                const renderWidth = Math.max(1, Math.round(cssWidth * pixelRatio));
                const renderHeight = Math.max(1, Math.round(cssHeight * pixelRatio));
                if (canvas.width !== renderWidth || canvas.height !== renderHeight) {
                    canvas.width = renderWidth;
                    canvas.height = renderHeight;
                }

                context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
                context.clearRect(0, 0, cssWidth, cssHeight);
                context.fillStyle = '#f5f5f5';
                context.fillRect(0, 0, cssWidth, cssHeight);

                const isRunning = phaseRef.current === 'running' && !pausedRef.current;
                if (isRunning) {
                    elapsedRef.current += delta;
                } else {
                    delta = 0;
                }

                const progress = clamp(elapsedRef.current / durationMs, 0, 1);
                const speed = (26 + (progress * 10)) * pace;
                const scroll = elapsedRef.current * speed / 1000;
                const laneWidth = cssWidth / 2;
                const cursorY = cssHeight * 0.72;
                const roadHalf = clamp(cssWidth * 0.038, 22, 31);
                const cursorRadius = clamp(cssHeight * 0.035, 8, 12);
                const amplitude = Math.min(laneWidth * 0.28, 58);
                const controlRange = (laneWidth / 2) - 24;
                const pathCenter = (lane, y) => {
                    const base = laneWidth * (lane === 0 ? 0.5 : 1.5);
                    const distance = (y - cursorY) + scroll;
                    return base + trackOffset(distance, amplitude, profilesRef.current[lane]);
                };

                context.strokeStyle = '#d9dee7';
                context.lineWidth = 1;
                context.beginPath();
                context.moveTo(laneWidth, 8);
                context.lineTo(laneWidth, cssHeight - 8);
                context.stroke();

                [0, 1].forEach((lane) => {
                    context.beginPath();
                    for (let y = -8; y <= cssHeight + 8; y += 4) {
                        const x = pathCenter(lane, y) - roadHalf;
                        if (y === -8) context.moveTo(x, y);
                        else context.lineTo(x, y);
                    }
                    for (let y = cssHeight + 8; y >= -8; y -= 4) {
                        context.lineTo(pathCenter(lane, y) + roadHalf, y);
                    }
                    context.closePath();
                    context.save();
                    context.shadowColor = 'rgba(31, 41, 55, 0.1)';
                    context.shadowBlur = 12;
                    context.fillStyle = '#ffffff';
                    context.fill();
                    context.restore();
                    context.strokeStyle = '#b8c1cf';
                    context.lineWidth = 2;
                    context.stroke();

                    context.beginPath();
                    context.setLineDash([5, 11]);
                    for (let y = -8; y <= cssHeight + 8; y += 4) {
                        const x = pathCenter(lane, y);
                        if (y === -8) context.moveTo(x, y);
                        else context.lineTo(x, y);
                    }
                    context.strokeStyle = '#e0e4ea';
                    context.lineWidth = 2;
                    context.stroke();
                    context.setLineDash([]);
                });

                const nextOutside = [0, 1].map((lane) => {
                    const laneBase = laneWidth * (lane === 0 ? 0.5 : 1.5);
                    const cursorX = laneBase + (controlsRef.current[lane] * controlRange);
                    const isOutside = cursorIsOutside({
                        controlRange,
                        controlValue: controlsRef.current[lane],
                        cursorRadius,
                        pathOffset: pathCenter(lane, cursorY) - laneBase,
                        roadHalf,
                    });
                    const accent = lane === 0 ? '#3c78e8' : '#f08a3e';

                    context.beginPath();
                    context.arc(cursorX, cursorY, cursorRadius + 4, 0, Math.PI * 2);
                    context.fillStyle = '#ffffff';
                    context.fill();
                    context.lineWidth = 2;
                    context.strokeStyle = isOutside ? '#d83b55' : '#d7dce5';
                    context.stroke();

                    context.beginPath();
                    context.arc(cursorX, cursorY, cursorRadius, 0, Math.PI * 2);
                    context.fillStyle = isOutside ? '#d83b55' : accent;
                    context.fill();

                    if (isOutside) {
                        context.beginPath();
                        context.moveTo(cursorX - 4, cursorY - 4);
                        context.lineTo(cursorX + 4, cursorY + 4);
                        context.moveTo(cursorX + 4, cursorY - 4);
                        context.lineTo(cursorX - 4, cursorY + 4);
                        context.strokeStyle = '#ffffff';
                        context.lineWidth = 2;
                        context.stroke();
                    }
                    return isOutside;
                });

                if (isRunning && delta > 0) {
                    trackedRef.current += delta * 2;
                    insideRef.current += delta * (2 - nextOutside.filter(Boolean).length);
                    nextOutside.forEach((isOutside, lane) => {
                        if (isOutside && !outsideRef.current[lane]) {
                            departuresRef.current += 1;
                            window.navigator.vibrate?.(28);
                        }
                    });
                }

                if (nextOutside.some((value, lane) => value !== outsideRef.current[lane])) {
                    outsideRef.current = nextOutside;
                    setOutside(nextOutside);
                }

                if (isRunning && now - lastMetricRef.current >= METRIC_INTERVAL_MS) {
                    lastMetricRef.current = now;
                    publishMetrics();
                }

                if (phaseRef.current === 'running' && elapsedRef.current >= durationMs) {
                    elapsedRef.current = durationMs;
                    phaseRef.current = 'done';
                    publishMetrics();
                    setPhase('done');
                }
            }

            frameRef.current = window.requestAnimationFrame(draw);
        };

        lastFrameRef.current = performance.now();
        frameRef.current = window.requestAnimationFrame(draw);
        return () => window.cancelAnimationFrame(frameRef.current);
    }, []);

    useEffect(() => {
        if (phase !== 'done' || completionReportedRef.current) {
            return;
        }

        completionReportedRef.current = true;
        onRunComplete?.(metrics);
    }, [metrics, onRunComplete, phase]);

    return (
        <GameShell
            {...props}
            copy={copy}
            disabled={isPaused}
            metrics={metrics}
            tone={outside.some(Boolean) ? 'miss' : 'ready'}
        >
            <div className={`UFOV-brain-games__dual-game ${outside.some(Boolean) ? 'is-collision' : ''}`.trim()}>
                <div className="UFOV-brain-games__dual-stage">
                    <canvas aria-label={copy.movingTracks} ref={canvasRef} />
                </div>
                <div className="UFOV-brain-games__game-cue">{copy.dualTrackCue}</div>
                <div className="UFOV-brain-games__dual-controls">
                    {[0, 1].map((index) => (
                        <DualControl
                            copy={copy}
                            index={index}
                            isOutside={outside[index]}
                            key={index}
                            onChange={setControl}
                            value={controls[index]}
                        />
                    ))}
                </div>
            </div>
            {phase === 'done' && showResults ? (
                <GameResultOverlay
                    hud={{ mistakes: true, score: true }}
                    metrics={metrics}
                    onRestart={reset}
                />
            ) : null}
        </GameShell>
    );
}

export default DualTrackGame;
